import React, { useState } from 'react';
import { Container, Typography, Card, CardContent, Stack, Chip, Box, Paper, Grid, List, ListItem, ListItemIcon, ListItemText, Tabs, Tab, Stepper, Step, StepLabel, useTheme, useMediaQuery } from '@mui/material';
import {
  SmartToy as SmartToyIcon,
  IntegrationInstructions as IntegrationInstructionsIcon,
  Storage as StorageIcon,
  Sync as SyncIcon,
  Api as ApiIcon,
  Hub as HubIcon,
  Search as SearchIcon,
  ArrowForward as ArrowForwardIcon,
  AccountTree as AccountTreeIcon,
  Autorenew as AutorenewIcon,
  PersonAdd as PersonAddIcon,
  GroupAdd as GroupAddIcon,
  PlayCircleOutline as PlayCircleOutlineIcon,
  Timeline as TimelineIcon,
  School as SchoolIcon,
  BuildCircle as BuildCircleIcon
} from '@mui/icons-material';

// --- Shared Components ---

function SectionHeader({ stepNumber, title, chips = [] }) {
  return (
    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
      {stepNumber !== undefined && <Chip label={stepNumber} color="primary" sx={{ fontWeight: 700 }} />}
      <Typography variant="h5" fontWeight={600}>{title}</Typography>
      <Stack direction="row" spacing={1} sx={{ ml: 'auto', flexWrap: 'wrap', gap: 1 }}>
        {chips.map((c, idx) => (
          <Chip key={idx} label={c} size="small" variant="outlined" color="primary" />
        ))}
      </Stack>
    </Stack>
  );
}

function ArchitectureCard({ icon, title, description, children, accentColor }) {
  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        transition: 'all 0.2s ease-in-out',
        borderTop: `4px solid ${accentColor}`,
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 4,
          borderColor: accentColor,
        },
      }}
    >
      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flexGrow: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
          <Box sx={{ color: accentColor, display: 'flex' }}>
            {icon}
          </Box>
          <Typography variant="subtitle1" fontWeight={700} color="text.primary">{title}</Typography>
        </Stack>
        {children ? (
          children
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line', lineHeight: 1.6 }}>{description}</Typography>
        )}
      </CardContent>
    </Card>
  );
}

function Section({ color = 'primary', children }) {
  return (
    <Paper
      elevation={0}
      variant="outlined"
      sx={{
        p: { xs: 3, sm: 4 },
        mb: 5,
        borderRadius: 2,
        borderLeft: (theme) => `8px solid ${theme.palette[color].main}`,
        backgroundColor: '#fafafa',
      }}
    >
      {children}
    </Paper>
  );
}

function StepFlowList({ items, color }) {
  return (
    <List dense disablePadding>
      {items.map((item, index) => (
        <ListItem key={index} alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
          <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
            <ArrowForwardIcon sx={{ fontSize: 16, color }} />
          </ListItemIcon>
          <ListItemText 
            primary={<Typography variant="body2" fontWeight={600} color="text.primary">{item.title}</Typography>}
            secondary={<Typography variant="body2" color="text.secondary">{item.desc}</Typography>}
            sx={{ m: 0 }}
          />
        </ListItem>
      ))}
    </List>
  );
}

// --- End User Workflow Tab ---

const endUserSteps = [
  {
    label: '1. Hire & Provision',
    icon: <PersonAddIcon color="primary" />,
    color: '#1976d2'
  },
  {
    label: '2. Project Assignment',
    icon: <GroupAddIcon color="secondary" />,
    color: '#9c27b0'
  },
  {
    label: '3. Task Execution',
    icon: <PlayCircleOutlineIcon color="success" />,
    color: '#2e7d32'
  },
  {
    label: '4. Tracking & ID',
    icon: <TimelineIcon color="warning" />,
    color: '#ed6c02'
  },
  {
    label: '5. Training & Memory',
    icon: <SchoolIcon color="error" />,
    color: '#d32f2f'
  }
];

function EndUserWorkflowTab() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h5" fontWeight={700} sx={{ mb: 1, color: 'text.primary' }}>
          The Manager's Perspective
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto' }}>
          Managing an AI Employee is designed to feel as simple and intuitive as managing a human teammate.
          Here is the detailed journey of how you define, hire, assign, and collaborate with your AI workforce in Tymeline.
        </Typography>
      </Box>

      {/* Visual Flow Diagram using Stepper */}
      <Paper variant="outlined" sx={{ p: 4, borderRadius: 2, backgroundColor: '#ffffff', mb: 6 }}>
        <Stepper orientation={isMobile ? 'vertical' : 'horizontal'} alternativeLabel={!isMobile}>
          {endUserSteps.map((step, index) => (
            <Step key={step.label} active={true}>
              <StepLabel 
                StepIconComponent={() => (
                  <Box sx={{ 
                    width: 48, height: 48, borderRadius: '50%', 
                    backgroundColor: `${step.color}15`, 
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: `2px solid ${step.color}`
                  }}>
                    {step.icon}
                  </Box>
                )}
              >
                <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 1, color: 'text.primary' }}>
                  {step.label}
                </Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Detailed Flow Sections */}
      <Section color="info">
        <SectionHeader title="Core Concepts" chips={["Terminology"]} />
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <ArchitectureCard icon={<SmartToyIcon />} title="AI Employee" accentColor="#0288d1" description="A digital worker in Tymeline acting as a standard employee." />
          </Grid>
          <Grid item xs={12} md={4}>
            <ArchitectureCard icon={<IntegrationInstructionsIcon />} title="AI Agents" accentColor="#0288d1" description="Specialized functional units utilized by the AI Employee to accomplish tasks." />
          </Grid>
          <Grid item xs={12} md={4}>
            <ArchitectureCard icon={<BuildCircleIcon />} title="AI Agent Tool" accentColor="#0288d1" description="External or internal tools (like GitHub, Cursor) used by the agents." />
          </Grid>
        </Grid>
      </Section>

      <Section color="primary">
        <SectionHeader stepNumber={1} title="AI Employee Creation" chips={["Hire"]} />
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <ArchitectureCard icon={<PersonAddIcon />} title="1. Role Selection & Verification" accentColor="#1976d2">
              <Typography variant="body2" color="text.secondary">
                Select a role from a predefined AI Employee role list. Tymeline then verifies:
              </Typography>
              <StepFlowList color="#1976d2" items={[
                { title: "Licenses", desc: "Checks if AI Employee licenses are available." },
                { title: "Tool Integrations", desc: "Verifies the company has enough agent-required tools integrated (e.g., Cursor, GitHub for software roles)." }
              ]} />
            </ArchitectureCard>
          </Grid>
          <Grid item xs={12}>
            <ArchitectureCard icon={<AccountTreeIcon />} title="2. Generation & Assignment" accentColor="#1976d2">
              <Typography variant="body2" color="text.secondary">
                Tymeline automatically generates basic details (name, description, skills, experience, avatar) and assigns required agents. After creation, it handles:
              </Typography>
              <StepFlowList color="#1976d2" items={[
                { title: "Tymeline ID", desc: "Creates a Tymeline ID account and assigns it to the AI Employee." },
                { title: "Team Binding", desc: "Creates a Tymeline User and adds them to the 'AI Employee Team', allowing them to act as a standard Employee/Team in any project." }
              ]} />
            </ArchitectureCard>
          </Grid>
        </Grid>
      </Section>

      <Section color="secondary">
        <SectionHeader stepNumber={2} title="AI Employee in Project" chips={["Assign"]} />
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <ArchitectureCard icon={<GroupAddIcon />} title="Project Suggestion & Assignment" accentColor="#9c27b0">
              <StepFlowList color="#9c27b0" items={[
                { title: "Suggestion", desc: "At project creation, Tymeline suggests the AI Employee team, or the user can choose manually." },
                { title: "Analysis", desc: "On next project analysis, Tymeline auto-assigns AI Employees to required roles." },
                { title: "Manual Override", desc: "Users can manually assign AI Employees or create new ones for the project if needed." }
              ]} />
            </ArchitectureCard>
          </Grid>
          <Grid item xs={12} md={6}>
            <ArchitectureCard icon={<ApiIcon />} title="Task Engine" accentColor="#9c27b0">
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                AI employees receive tasks from the Tymeline task engine just like human employees. However, these tasks remain internal and do not sync to external project management tools, as the AI employee is not technically part of the external PM team.
              </Typography>
            </ArchitectureCard>
          </Grid>
        </Grid>
      </Section>

      <Section color="success">
        <SectionHeader stepNumber={3} title="Story / Task Execution" chips={["Execute"]} />
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <ArchitectureCard icon={<PlayCircleOutlineIcon />} title="Invocation & Review" accentColor="#2e7d32">
              <StepFlowList color="#2e7d32" items={[
                { title: "Auto/Manual Execution", desc: "Tymeline auto-invokes the AI when task time arrives, but users can also manually execute tasks from the project dashboard." },
                { title: "Review Status", desc: "Once executed, Epic/Story status changes to 'Review', where users can review and approve the PR (for software roles)." }
              ]} />
            </ArchitectureCard>
          </Grid>
          <Grid item xs={12} md={6}>
            <ArchitectureCard icon={<TimelineIcon />} title="Proof of Execution" accentColor="#2e7d32">
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Upon Epic completion, all participating AI employees receive a Tymeline ID record for the Epic. This acts as their Proof of Execution and permanent record of contribution.
              </Typography>
            </ArchitectureCard>
          </Grid>
        </Grid>
      </Section>

      <Section color="warning">
        <SectionHeader stepNumber={4} title="Activity Tracker & Tymeline ID" chips={["Track"]} />
        <ArchitectureCard icon={<SearchIcon />} title="Complete Observability" accentColor="#ed6c02">
          <Typography variant="body2" color="text.secondary">
            All AI Employee work-related activities are tracked and securely saved. Authorized users can view this comprehensive timeline at any time to audit the AI's decisions and actions.
          </Typography>
        </ArchitectureCard>
      </Section>

      <Section color="error">
        <SectionHeader stepNumber={5} title="Reinforcement & Memory" chips={["Train"]} />
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <ArchitectureCard icon={<SchoolIcon />} title="Reinforcement Loop" accentColor="#d32f2f">
              <StepFlowList color="#d32f2f" items={[
                { title: "Via Interaction", desc: "Reinforce work directly on PRs by interacting with Via on GitHub (e.g., tag @via with specific instructions)." },
                { title: "Immediate Execution", desc: "The reinforcement is executed and remembered for future similar tasks." }
              ]} />
            </ArchitectureCard>
          </Grid>
          <Grid item xs={12} md={6}>
            <ArchitectureCard icon={<StorageIcon />} title="Dual Memory System" accentColor="#d32f2f">
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Every AI Employee maintains its own isolated memory base while also tapping into a shared Project Memory, ensuring consistent context across the entire AI workforce.
              </Typography>
            </ArchitectureCard>
          </Grid>
        </Grid>
      </Section>

    </Box>
  );
}

// --- Technical Architecture Tab ---

function TechnicalArchitectureTab() {
  return (
    <Box>
      {/* 1. Core Architectural Entities */}
      <Section color="info">
        <SectionHeader title="1. Core Entities & Memory Architecture" chips={["workforce-service"]} />
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <ArchitectureCard 
              icon={<SmartToyIcon />} 
              title="AI Employee Model" 
              accentColor="#0288d1"
            >
              <Typography variant="body2" color="text.secondary">
                A business-scoped virtual team member containing identity bindings and configuration:
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 1 }}>
                {['kwickly_account_id', 'tymeline_id', 'license_id', 'agent_ids', 'role_config'].map(tag => (
                  <Chip key={tag} label={tag} size="small" sx={{ fontSize: '0.7rem', fontFamily: 'monospace' }} />
                ))}
              </Stack>
            </ArchitectureCard>
          </Grid>
          <Grid item xs={12} md={4}>
            <ArchitectureCard 
              icon={<IntegrationInstructionsIcon />} 
              title="Agents & Tools" 
              accentColor="#0288d1"
              description="Executable worker units attached to roles (e.g., Coder, HR Assistant, Project Reporter). Agents are bound to specific capability tools (Cursor, GitHub, Web Search, SMTP)."
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <ArchitectureCard 
              icon={<StorageIcon />} 
              title="Dual-Lane Memory Model" 
              accentColor="#0288d1"
            >
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Memory retrieved during task planning via Vector Search:
              </Typography>
              <StepFlowList color="#0288d1" items={[
                { title: "Employee Lane", desc: "Episodic and semantic memory specific to this AI Employee." },
                { title: "Project Shared Lane", desc: "Shared context accessible by all AI Employees on the project." }
              ]} />
            </ArchitectureCard>
          </Grid>
        </Grid>
      </Section>

      {/* 2. Creation & Provisioning Lifecycle */}
      <Section color="primary">
        <SectionHeader stepNumber={2} title="Creation & Provisioning Lifecycle" chips={["Orchestration", "Idempotency"]} />
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <ArchitectureCard 
              icon={<AccountTreeIcon />} 
              title="Distributed Orchestration Pipeline" 
              accentColor="#1976d2"
            >
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Creation uses a <code>(business_id, email)</code> idempotency lock and progresses through multiple external services:
              </Typography>
              <StepFlowList color="#1976d2" items={[
                { title: "Payment Service", desc: "Reserves an AI Employee license before creation." },
                { title: "Integration Service", desc: "Validates prerequisites (e.g., GitHub connection for Software roles)." },
                { title: "Kwickly", desc: "Creates/resolves AI user and assigns to the shared 'AI employees' team." },
                { title: "Tymeline ID Service", desc: "Provisions the secure Tymeline identity token." },
                { title: "NewTools Service", desc: "Async trigger to refresh cross-system team summaries." }
              ]} />
            </ArchitectureCard>
          </Grid>
          <Grid item xs={12} md={6}>
            <ArchitectureCard 
              icon={<HubIcon />} 
              title="Role-Based Auto-Fill" 
              accentColor="#1976d2"
              description="When creating an AI Employee, the system auto-fills configuration via LLM-based semantic matching. It evaluates the selected role title and generates persona data, binding the required default capabilities (like Coder agents for Software Engineers)."
            />
          </Grid>
        </Grid>
      </Section>

      {/* 3. Task Execution Architecture */}
      <Section color="secondary">
        <SectionHeader stepNumber={3} title="Execution Pipeline & Reconciliation" chips={["gRPC", "Async/Sync"]} />
        <Grid container spacing={3}>
          <Grid item xs={12} md={5}>
            <ArchitectureCard 
              icon={<ApiIcon />} 
              title="State Machine & Entry Points" 
              accentColor="#9c27b0"
            >
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Triggered via gRPC (`ExecuteAIEmployeeTask`, `ExecuteAIEmployeeStoryWithTasks`). State flows from:
              </Typography>
              <Box sx={{ p: 1.5, bgcolor: '#f3e5f5', borderRadius: 1, mb: 1, fontFamily: 'monospace', fontSize: '0.8rem', textAlign: 'center' }}>
                pending → planning → running → completed/failed
              </Box>
              <Typography variant="body2" color="text.secondary">
                Pipeline execution validates capabilities, recalls vector memory, and builds an LLM execution plan before launching agents.
              </Typography>
            </ArchitectureCard>
          </Grid>
          <Grid item xs={12} md={7}>
            <ArchitectureCard 
              icon={<SyncIcon />} 
              title="Asynchronous Execution Branches" 
              accentColor="#9c27b0"
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Software Roles (Cursor Agent)</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Launches Cursor cloud agent asynchronously. The task remains `running` while the external run progresses. A cron-driven polling mechanism (`ReconcileCursorAgentTasks`) periodically checks for completion. Expected outcome is a GitHub PR requiring human approval.
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>HR Assistant (Synchronous)</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Specialized execution branch handling intents like QA, Semantic Search, and Job Posting. Relies on internal ATS/HRIS integrations and returns direct model answers synchronously in the task response.
                  </Typography>
                </Grid>
              </Grid>
            </ArchitectureCard>
          </Grid>
        </Grid>
      </Section>

      {/* 4. Reinforcement Learning */}
      <Section color="error">
        <SectionHeader stepNumber={4} title="Reinforcement Memory Loop" chips={["LLM", "MongoDB"]} />
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <ArchitectureCard 
              icon={<SearchIcon />} 
              title="Feedback Ingestion" 
              accentColor="#d32f2f"
            >
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                The AI Employee continuously improves through human feedback:
              </Typography>
              <StepFlowList color="#d32f2f" items={[
                { title: "Via Bridge", desc: "Captures feedback from GitHub PR comments tagged with `@via` or via chat commands." },
                { title: "Extraction", desc: "Service uses an LLM to extract rule, category, scope, and confidence level." },
                { title: "Persistence", desc: "Indexes the extracted rule in Mongo as a vector with `memory_kind=reinforcement`." }
              ]} />
            </ArchitectureCard>
          </Grid>
          <Grid item xs={12} md={6}>
            <ArchitectureCard 
              icon={<AutorenewIcon />} 
              title="Context Injection & Safety" 
              accentColor="#d32f2f"
            >
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                During the execution recall phase, reinforcement rules act as absolute constraints:
              </Typography>
              <StepFlowList color="#d32f2f" items={[
                { title: "Pinned Constraints", desc: "Reinforcement rules are injected as pinned 'must follow' blocks above normal memory context." },
                { title: "Guardrails", desc: "Includes kill-switches, confidence thresholds, fanout caps, and strict token budgeting to ensure safe rollout." },
                { title: "Retention", desc: "Cron-driven jobs clean up stale episodic and vector records daily." }
              ]} />
            </ArchitectureCard>
          </Grid>
        </Grid>
      </Section>
    </Box>
  );
}

// --- Main Page Component ---

export default function AIEmployeeWorkflow() {
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h3" fontWeight={800} sx={{ mb: 2, color: 'text.primary', letterSpacing: '-0.02em' }}>
          AI Employee Workflow
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto', lineHeight: 1.6 }}>
          Explore the lifecycle of an AI Employee. Toggle between the End-User perspective and the underlying Production Architecture.
        </Typography>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4, display: 'flex', justifyContent: 'center' }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="workflow views" centered>
          <Tab label="End User Experience" sx={{ fontWeight: 600, fontSize: '1rem', textTransform: 'none' }} />
          <Tab label="Production Architecture" sx={{ fontWeight: 600, fontSize: '1rem', textTransform: 'none' }} />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <Box role="tabpanel" hidden={activeTab !== 0}>
        {activeTab === 0 && <EndUserWorkflowTab />}
      </Box>
      <Box role="tabpanel" hidden={activeTab !== 1}>
        {activeTab === 1 && <TechnicalArchitectureTab />}
      </Box>
    </Container>
  );
}
